import React, { useState } from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { useSettingsObject } from '../../../settings/hooks';
import { getLighterThemeColor, getUserThemeColor, shadeColor, toRgba } from '../../../../ui/theme/colors';

const Container = styled(Flex)`
  background: transparent;
  padding: 0;
`;

const Header = styled(Flex)`
  margin-bottom: 16px;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepItem = styled(Flex)`
  padding: 12px 16px;
  border-radius: 8px;
  background: ${(props) => (props.$completed ? theme.colors.NEU_100 : theme.colors.NEU_0)};
  border: 1px solid ${theme.colors.NEU_200};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$completed ? 0.6 : 1)};

  &:hover {
    background: ${theme.colors.NEU_100};
    box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.06)};
    border-color: ${theme.colors.NEU_300};
  }
`;

const StepContent = styled(Flex)`
  flex: 1;
`;

const PriorityBadge = styled.div`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    if (props.$priority === 'high') return shadeColor(theme.colors.ACTION, 0.92);
    if (props.$priority === 'medium') return shadeColor(theme.colors.SECONDARY, 0.92);
    return theme.colors.NEU_200;
  }};
  color: ${(props) => {
    if (props.$priority === 'high') return theme.colors.ACTION;
    if (props.$priority === 'medium') return theme.colors.SECONDARY;
    return theme.colors.NEU_600;
  }};
`;

const CategoryTag = styled.div`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${(props) => props.$brandColor || theme.colors.PRIMARY};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.NEU_200};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 16px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  width: ${(props) => props.$progress}%;
  transition: width 0.3s ease;
  border-radius: 3px;
`;

const FilterButtons = styled(Flex)`
  gap: 6px;
  margin-bottom: 12px;
`;

const FilterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.$active ? props.$brandColor || theme.colors.PRIMARY : theme.colors.NEU_300)};
  background: ${(props) => (props.$active ? props.$brandColor || theme.colors.PRIMARY : 'transparent')};
  color: ${(props) => (props.$active ? theme.colors.NEU_0 : theme.colors.NEU_600)};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
    background: ${(props) => (props.$active ? props.$brandColor || theme.colors.PRIMARY : theme.colors.NEU_100)};
  }
`;

const ActionSteps = ({ actionPlan }) => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;

  const steps = actionPlan?.steps || [];

  const [filter, setFilter] = useState('all');
  const [localSteps, setLocalSteps] = useState(steps);

  const toggleStep = (stepId) => {
    setLocalSteps((prevSteps) => prevSteps.map((step) => (step.id === stepId ? { ...step, completed: !step.completed } : step)));
  };

  const filteredSteps = localSteps.filter((step) => {
    if (filter === 'pending') return !step.completed;
    if (filter === 'completed') return step.completed;
    return true;
  });

  const completedCount = localSteps.filter((step) => step.completed).length;
  const totalCount = localSteps.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Container direction='column'>
      <Header direction='column' gap='8'>
        <Flex justify='space-between' align='center' gap='16'>
          <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
            Action Plan
          </Typo.MediumText>
          <Typo.SmallText color={theme.colors.NEU_600} fontWeight='500' style={{ flexShrink: 0 }}>
            {completedCount}/{totalCount} done
          </Typo.SmallText>
        </Flex>
        <ProgressBar>
          <ProgressFill $progress={progressPercentage} $brandColor={brandColor} />
        </ProgressBar>
      </Header>

      <FilterButtons>
        <FilterButton $active={filter === 'all'} $brandColor={brandColor} onClick={() => setFilter('all')}>
          All ({totalCount})
        </FilterButton>
        <FilterButton $active={filter === 'pending'} $brandColor={brandColor} onClick={() => setFilter('pending')}>
          Pending ({totalCount - completedCount})
        </FilterButton>
        <FilterButton $active={filter === 'completed'} $brandColor={brandColor} onClick={() => setFilter('completed')}>
          Completed ({completedCount})
        </FilterButton>
      </FilterButtons>

      <StepsList>
        {filteredSteps.length === 0 ? (
          <Flex justify='center' align='center' style={{ padding: '32px 0' }}>
            <Typo.Info color={theme.colors.NEU_600}>{filter === 'completed' ? 'No completed steps yet' : 'No pending steps'}</Typo.Info>
          </Flex>
        ) : (
          filteredSteps.map((step) => (
            <StepItem key={step.id} $completed={step.completed} $priority={step.priority} onClick={() => toggleStep(step.id)} align='flex-start' gap='12'>
              <StepContent direction='column' gap='8' justify='flext-start'>
                <Flex direction='column' align='flex-start' width='100%' gap='4'>
                  <Flex justify='space-between' align='flex-start' gap='12' width='100%'>
                    <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
                      {step.title}
                    </Typo.MediumText>
                    <Flex gap='6' style={{ flexShrink: 0 }}>
                      <PriorityBadge $priority={step.priority}>{step.priority}</PriorityBadge>
                    </Flex>
                  </Flex>

                  <Typo.Info color={theme.colors.NEU_600}>{step.description}</Typo.Info>
                </Flex>

                <CategoryTag $brandColor={getLighterThemeColor(brandColor)}>
                  <Typo.Info $themeColor={getLighterThemeColor(brandColor)}>{step.category}</Typo.Info>
                </CategoryTag>
              </StepContent>
            </StepItem>
          ))
        )}
      </StepsList>
    </Container>
  );
};

export default ActionSteps;
